// ============================================
// PRESENTATION TYPES
// ============================================

export type SlideBackground = 
  | 'gradient-primary'
  | 'gradient-secondary'
  | 'gradient-accent'
  | 'gradient-dark'
  | 'white'
  | 'gray'
  | string;

export type SlideTransition = 
  | 'slide'
  | 'fade'
  | 'zoom'
  | 'flip'
  | 'none';

export type SlideAnimation = 
  | 'fade-in'
  | 'fade-in-fast'
  | 'fade-in-slow'
  | 'slide-in'
  | 'slide-in-left'
  | 'slide-in-right'
  | 'slide-in-up'
  | 'slide-in-down'
  | 'zoom-in'
  | 'zoom-out'
  | 'bounce-in'
  | 'none';

export type SlideLayout = 
  | 'center'
  | 'left'
  | 'right'
  | 'top'
  | 'bottom'
  | 'full';

export interface SlideData {
  id: number;
  title?: string;
  subtitle?: string;
  content?: React.ReactNode;
  background?: SlideBackground;
  transition?: SlideTransition;
  animation?: SlideAnimation;
  layout?: SlideLayout;
  component?: React.ComponentType<unknown>;
  props?: Record<string, unknown>;
  /** Presenter notes - only visible in presenter mode */
  notes?: string;
  /** Duration in seconds for this slide (for auto-play and presenter timing) */
  duration?: number;
}

export interface PresentationMetadata {
  title: string;
  subtitle?: string;
  author?: string;
  date?: string;
  description?: string;
  tags?: string[];
  duration?: number; // in minutes
}

export interface PresentationData {
  metadata: PresentationMetadata;
  slides: SlideData[];
}

export interface PresentationState {
  currentSlide: number;
  totalSlides: number;
  isFullscreen: boolean;
  isOverview: boolean;
  isPaused: boolean;
  transitionType: SlideTransition;
  direction: 'forward' | 'backward';
}

export interface PresentationControls {
  goToSlide: (index: number) => void;
  nextSlide: () => void;
  previousSlide: () => void;
  toggleFullscreen: () => void;
  toggleOverview: () => void;
  togglePause: () => void;
  setTransition: (transition: SlideTransition) => void;
}

// ============================================
// UI COMPONENT TYPES
// ============================================

export type ButtonVariant = 
  | 'primary'
  | 'secondary'
  | 'accent'
  | 'ghost'
  | 'outline'
  | 'link';

export type ButtonSize = 
  | 'sm'
  | 'md'
  | 'lg'
  | 'xl';

export type CardVariant = 
  | 'default'
  | 'bordered'
  | 'elevated'
  | 'gradient'
  | 'glass';

export type ContainerSize = 
  | 'sm'
  | 'md'
  | 'lg'
  | 'xl'
  | '2xl'
  | 'full';

export type BadgeVariant = 
  | 'primary'
  | 'secondary'
  | 'accent'
  | 'success'
  | 'warning'
  | 'error'
  | 'info';

export type IconSize = 
  | 'xs'
  | 'sm'
  | 'md'
  | 'lg'
  | 'xl'
  | '2xl'
  | '3xl';

export interface ImageProps {
  src: string;
  alt: string;
  aspectRatio?: '16:9' | '4:3' | '1:1' | '3:4' | string;
  objectFit?: 'cover' | 'contain' | 'fill' | 'none' | 'scale-down';
  overlay?: 'none' | 'gradient' | 'dark' | 'light';
  className?: string;
}

// ============================================
// NAVIGATION TYPES
// ============================================

export interface NavigationItem {
  label: string;
  href?: string;
  onClick?: () => void;
  icon?: React.ReactNode;
  active?: boolean;
  disabled?: boolean;
}

export interface FooterLink {
  label: string;
  href: string;
  external?: boolean;
}

export interface SocialLink {
  platform: 'github' | 'linkedin' | 'twitter' | 'email' | 'website';
  url: string;
  icon?: React.ReactNode;
}

// ============================================
// UTILITY TYPES
// ============================================

export type ClassName = string | undefined | null | false;

export type Children = React.ReactNode;

export interface BaseComponentProps {
  className?: string;
  children?: React.ReactNode;
  style?: React.CSSProperties;
  id?: string;
}

// ============================================
// PRESENTER MODE TYPES
// ============================================

export interface PresenterModeState {
  isActive: boolean;
  showNotes: boolean;
  showNextSlide: boolean;
  showTimer: boolean;
  elapsedSeconds: number;
}

export interface PresenterTimerState {
  elapsed: number;
  isRunning: boolean;
}

// ============================================
// EXPORT TYPES
// ============================================

export type ExportFormat = 'pdf' | 'html' | 'json';

export interface ExportOptions {
  format: ExportFormat;
  includeNotes: boolean;
  includeSlideNumbers: boolean;
  quality: 'low' | 'medium' | 'high';
}

export interface ExportProgress {
  isExporting: boolean;
  progress: number;
  stage: string;
}

export interface ExportResult {
  success: boolean;
  downloadUrl?: string;
  error?: string;
}

// ============================================
// KEYBOARD SHORTCUTS TYPES
// ============================================

export interface KeyboardShortcut {
  key: string;
  description: string;
  action: string;
}

export const KEYBOARD_SHORTCUTS: KeyboardShortcut[] = [
  { key: '→ / ↓ / Space', description: 'Next slide', action: 'nextSlide' },
  { key: '← / ↑', description: 'Previous slide', action: 'previousSlide' },
  { key: 'Home', description: 'First slide', action: 'firstSlide' },
  { key: 'End', description: 'Last slide', action: 'lastSlide' },
  { key: 'F', description: 'Toggle fullscreen', action: 'toggleFullscreen' },
  { key: 'P', description: 'Toggle presenter mode', action: 'togglePresenter' },
  { key: 'T', description: 'Toggle thumbnails', action: 'toggleThumbnails' },
  { key: 'E', description: 'Open export menu', action: 'showExport' },
  { key: 'N', description: 'Toggle notes (in presenter mode)', action: 'toggleNotes' },
  { key: '1-9', description: 'Jump to slide', action: 'jumpToSlide' },
  { key: '?', description: 'Show keyboard shortcuts', action: 'showHelp' },
  { key: 'ESC', description: 'Exit fullscreen/close help', action: 'exit' },
];
